import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG, type BoardType } from '@/(routers)/boards/editor/block/blockConfig';
import { expr, registerSetupCode, resolveBoard, stmt } from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import { variableDeclarations } from '@/(routers)/boards/editor/block/generators/arduino/blocks/variable/variable';

type Position = keyof (typeof BOARD_CONFIG)[BoardType]['pins'];

type PcintConfig = {
    vect: 'PCINT0_vect' | 'PCINT1_vect' | 'PCINT2_vect';
    pinReg: 'PINB' | 'PINC' | 'PIND';
    portBit: string;
    pcintBit: string;
    pcicr: string;
    pcmsk: string;
};

const ORANGE_PCINT_CONFIG: Record<number, PcintConfig> = {
    19: { vect: 'PCINT1_vect', pinReg: 'PINC', portBit: 'PC5', pcintBit: 'PCINT13', pcicr: 'PCIE1', pcmsk: 'PCMSK1' }, // 左上
    15: { vect: 'PCINT1_vect', pinReg: 'PINC', portBit: 'PC0', pcintBit: 'PCINT8', pcicr: 'PCIE1', pcmsk: 'PCMSK1' }, // 右上
    5: { vect: 'PCINT2_vect', pinReg: 'PIND', portBit: 'PD5', pcintBit: 'PCINT21', pcicr: 'PCIE2', pcmsk: 'PCMSK2' }, // 左下
    10: { vect: 'PCINT0_vect', pinReg: 'PINB', portBit: 'PB2', pcintBit: 'PCINT2', pcicr: 'PCIE0', pcmsk: 'PCMSK0' }, // 右下
};

function buildOrangeIsr(vect: string, pins: number[]): string {
    const lines: string[] = [];
    for (const pin of pins) {
        const c = ORANGE_PCINT_CONFIG[pin];
        lines.push(
            `  uint8_t cur_${pin} = ${c.pinReg} & (1 << ${c.portBit});`,
            `  if (!_wfLastPinState_${pin} && cur_${pin}) _wfTotalPulseCount_${pin}++;`,
            `  _wfLastPinState_${pin} = cur_${pin};`,
        );
    }
    return `ISR(${vect}) {\n${lines.join('\n')}\n}\n`;
}

const WATER_FLOW_GLOBAL_VARS_ORANGE = (pin: number) =>
    `volatile uint8_t _wfLastPinState_${pin} = 0;
volatile uint32_t _wfTotalPulseCount_${pin} = 0;
unsigned long _wfTotalPulsePerSecond_${pin} = 0;
unsigned long _wfPreviousMillis_${pin} = 0;
const uint32_t _wfIntervalTime_${pin} = 1000;
float _wfPulsePerSecond_${pin} = 0;
`;

const WATER_FLOW_SETUP_FN_ORANGE = (pin: number) => {
    const c = ORANGE_PCINT_CONFIG[pin];
    return `void _wfSetup_${pin}() {
  PCICR  |= (1 << ${c.pcicr});
  ${c.pcmsk} |= (1 << ${c.pcintBit});
  _wfLastPinState_${pin} = ${c.pinReg} & (1 << ${c.portBit});
  sei();
}
`;
};

const WATER_FLOW_GLOBAL_BASE_BLUE = (pin: number) =>
    `portMUX_TYPE _wfCounterMux_${pin} = portMUX_INITIALIZER_UNLOCKED;
volatile uint32_t _wfTotalPulseCount_${pin} = 0;
unsigned long _wfTotalPulsePerSecond_${pin} = 0;
unsigned long _wfPreviousMillis_${pin} = 0;
const uint32_t _wfIntervalTime_${pin} = 1000;
float _wfPulsePerSecond_${pin} = 0;
void IRAM_ATTR _wfCounter_${pin}() {
  portENTER_CRITICAL_ISR(&_wfCounterMux_${pin});
  _wfTotalPulseCount_${pin}++;
  portEXIT_CRITICAL_ISR(&_wfCounterMux_${pin});
}
void _wfSetup_${pin}(int pinNumber) {
  attachInterrupt(digitalPinToInterrupt(pinNumber), _wfCounter_${pin}, RISING);
}
`;

const WATER_FLOW_PPS_ORANGE = (pin: number) =>
    `float _wfGetPulsePerSecond_${pin}() {
  noInterrupts();
  uint32_t currentTotal = _wfTotalPulseCount_${pin};
  interrupts();
  unsigned long currentMillis = millis();
  if (currentMillis - _wfPreviousMillis_${pin} >= _wfIntervalTime_${pin}) {
    _wfPulsePerSecond_${pin} = currentTotal - _wfTotalPulsePerSecond_${pin};
    _wfTotalPulsePerSecond_${pin} = currentTotal;
    _wfPreviousMillis_${pin} = currentMillis;
  }
  return _wfPulsePerSecond_${pin};
}
`;

const WATER_FLOW_PPS_BLUE = (pin: number) =>
    `float _wfGetPulsePerSecond_${pin}() {
  portENTER_CRITICAL(&_wfCounterMux_${pin});
  uint32_t currentTotal = _wfTotalPulseCount_${pin};
  portEXIT_CRITICAL(&_wfCounterMux_${pin});
  unsigned long currentMillis = millis();
  if (currentMillis - _wfPreviousMillis_${pin} >= _wfIntervalTime_${pin}) {
    _wfPulsePerSecond_${pin} = currentTotal - _wfTotalPulsePerSecond_${pin};
    _wfTotalPulsePerSecond_${pin} = currentTotal;
    _wfPreviousMillis_${pin} = currentMillis;
  }
  return _wfPulsePerSecond_${pin};
}
`;

const WATER_FLOW_TOTAL_ORANGE = (pin: number) =>
    `float _wfGetTotalPulse_${pin}() {
  noInterrupts();
  float total = _wfTotalPulseCount_${pin};
  interrupts();
  return total;
}
`;

const WATER_FLOW_TOTAL_BLUE = (pin: number) =>
    `float _wfGetTotalPulse_${pin}() {
  portENTER_CRITICAL(&_wfCounterMux_${pin});
  float total = _wfTotalPulseCount_${pin};
  portEXIT_CRITICAL(&_wfCounterMux_${pin});
  return total;
}
`;

export const waterFlowMeasurementGlobalCodes = new Map<string, string>();

// ベクタごとに登録済みピンを追跡（ISR共有管理用）
const _vectPins = new Map<string, number[]>();

export function resetWaterFlowMeasurement(): void {
    waterFlowMeasurementGlobalCodes.clear();
    _vectPins.clear();
}

function registerOrangePin(pin: number): void {
    const cfg = ORANGE_PCINT_CONFIG[pin];
    const vect = cfg.vect;

    waterFlowMeasurementGlobalCodes.set(`vars_${pin}`, WATER_FLOW_GLOBAL_VARS_ORANGE(pin));
    waterFlowMeasurementGlobalCodes.set(`setup_fn_${pin}`, WATER_FLOW_SETUP_FN_ORANGE(pin));

    // ベクタに追加してISRを再生成（同一ベクタの全ピンをまとめる）
    if (!_vectPins.has(vect)) _vectPins.set(vect, []);
    const pins = _vectPins.get(vect)!;
    if (!pins.includes(pin)) {
        pins.push(pin);
    }
    waterFlowMeasurementGlobalCodes.set(`isr_${vect}`, buildOrangeIsr(vect, pins));
}

export function register(gen: any): void {
    gen.forBlock['water_flow_measurement'] = function (block: Blockly.Block): string | [string, number] {
        const position = block.getFieldValue('POSITION') as Position;
        const mode = block.getFieldValue('MODE') as string;
        const board = resolveBoard(block);
        const pin = BOARD_CONFIG[board].pins[position].second;

        if (mode === 'PULSE_PER_SECOND') {
            if (board === 'orange') {
                registerOrangePin(pin);
                waterFlowMeasurementGlobalCodes.set(`pps_${pin}`, WATER_FLOW_PPS_ORANGE(pin));
                registerSetupCode(`wf_setup_${pin}`, `  pinMode(${pin}, INPUT_PULLUP);\n  _wfSetup_${pin}();\n`);
            } else {
                waterFlowMeasurementGlobalCodes.set(`base_${pin}`, WATER_FLOW_GLOBAL_BASE_BLUE(pin));
                waterFlowMeasurementGlobalCodes.set(`pps_${pin}`, WATER_FLOW_PPS_BLUE(pin));
                registerSetupCode(`wf_setup_${pin}`, `  pinMode(${pin}, INPUT_PULLUP);\n  _wfSetup_${pin}(${pin});\n`);
            }
            return expr(`_wfGetPulsePerSecond_${pin}()`);
        } else {
            const varName = block.getFieldValue('TOTAL_VAR') as string;
            if (!varName || varName === '設定する変数') return '';

            if (board === 'orange') {
                registerOrangePin(pin);
                waterFlowMeasurementGlobalCodes.set(`total_${pin}`, WATER_FLOW_TOTAL_ORANGE(pin));
                registerSetupCode(`wf_setup_${pin}`, `  pinMode(${pin}, INPUT_PULLUP);\n  _wfSetup_${pin}();\n`);
            } else {
                waterFlowMeasurementGlobalCodes.set(`base_${pin}`, WATER_FLOW_GLOBAL_BASE_BLUE(pin));
                waterFlowMeasurementGlobalCodes.set(`total_${pin}`, WATER_FLOW_TOTAL_BLUE(pin));
                registerSetupCode(`wf_setup_${pin}`, `  pinMode(${pin}, INPUT_PULLUP);\n  _wfSetup_${pin}(${pin});\n`);
            }
            variableDeclarations.set(varName, `float ${varName} = 0;\n`);
            return stmt(`${varName} = _wfGetTotalPulse_${pin}()`);
        }
    };
}
